import { useRouter } from 'expo-router';
import { useEffect } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import Animated, {
  useAnimatedProps,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import Svg, { Circle } from 'react-native-svg';

import { Fonts, Palette } from '@/constants/theme';

const AnimatedCircle = Animated.createAnimatedComponent(Circle);

const SIZE = 52;
const STROKE = 5;
const RADIUS = (SIZE - STROKE) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

/**
 * Honey-colored ring next to the profile ring. Shows percent of the
 * current season's tasks completed — a fresh setup starts at 0% and
 * climbs as the user knocks things off. The label below names the season
 * so the meaning is glanceable.
 */
export function SeasonRing({
  pct,
  season,
}: {
  pct: number;
  season: 'Spring' | 'Summer' | 'Fall' | 'Winter';
}) {
  const router = useRouter();
  const target = Math.max(0, Math.min(100, pct));
  const progress = useSharedValue(0);

  useEffect(() => {
    progress.value = withTiming(target / 100, { duration: 720 });
  }, [target, progress]);

  const animatedProps = useAnimatedProps(() => ({
    strokeDashoffset: CIRCUMFERENCE * (1 - progress.value),
  }));

  const openSeason = () => {
    router.push({
      pathname: '/(tabs)/schedule',
      params: { season: season.toLowerCase() },
    } as never);
  };

  return (
    <Pressable
      onPress={openSeason}
      hitSlop={6}
      accessibilityRole="button"
      accessibilityLabel={`${season} season tasks — tap to filter the schedule`}
      style={({ pressed }) => pressed && { opacity: 0.85 }}>
      <View style={styles.wrap}>
        <Svg width={SIZE} height={SIZE} style={styles.svg}>
          <Circle
            cx={SIZE / 2}
            cy={SIZE / 2}
            r={RADIUS}
            stroke={Palette.line}
            strokeWidth={STROKE}
            fill="none"
          />
          <AnimatedCircle
            cx={SIZE / 2}
            cy={SIZE / 2}
            r={RADIUS}
            stroke={Palette.honey}
            strokeWidth={STROKE}
            strokeLinecap="round"
            fill="none"
            strokeDasharray={CIRCUMFERENCE}
            animatedProps={animatedProps}
            transform={`rotate(-90 ${SIZE / 2} ${SIZE / 2})`}
          />
        </Svg>
        <View style={styles.pctWrap} pointerEvents="none">
          <Text style={styles.pctText}>{target}%</Text>
        </View>
      </View>
      <Text style={styles.label}>{season.toUpperCase()}</Text>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  wrap: {
    width: SIZE,
    height: SIZE,
  },
  svg: {
    position: 'absolute',
    top: 0,
    left: 0,
  },
  pctWrap: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    justifyContent: 'center',
  },
  pctText: {
    fontFamily: Fonts.sansBold,
    fontSize: 13,
    color: Palette.honeyDeep,
  },
  label: {
    marginTop: 1,
    textAlign: 'center',
    fontSize: 10.5,
    fontFamily: Fonts.sansBold,
    // Mid-grey instead of inkFaint so the season name reads cleanly
    // against the cream backdrop.
    color: Palette.inkSoft,
    letterSpacing: 0.6,
  },
});
